// src/utils/sound.js
// Son + notification système pour les événements temps réel.
// - Aucune demande de permission au chargement : les navigateurs bloquent (et
//   pénalisent) les demandes sans geste utilisateur. La permission est demandée
//   depuis le bouton « Son » de la cloche.
// - L'audio est déverrouillé au premier geste utilisateur (politique autoplay).

const NOTIF_SOUND = '/assets/sounds/s2.wav';
const SOUND_PREF_KEY = 'notificationSoundEnabled';
// Plusieurs événements peuvent arriver d'un coup (message + feedback, rafale
// de messages) : un seul son par fenêtre.
const SOUND_THROTTLE_MS = 1500;
const OS_NOTIF_TIMEOUT_MS = 5000;

let notifAudio = null;
let audioUnlocked = false;
let initialized = false;
let lastPlayedAt = 0;

const UNLOCK_EVENTS = ['click', 'touchend', 'keydown'];

function unlockAudio() {
  if (!notifAudio || audioUnlocked) return;
  const { volume } = notifAudio;
  notifAudio.volume = 0;
  notifAudio
    .play()
    .then(() => {
      notifAudio.pause();
      notifAudio.currentTime = 0;
      audioUnlocked = true;
      UNLOCK_EVENTS.forEach((evt) => window.removeEventListener(evt, unlockAudio, true));
    })
    .catch(() => {})
    .finally(() => {
      notifAudio.volume = volume;
    });
}

export function initSounds() {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  try {
    notifAudio = new Audio(NOTIF_SOUND);
    notifAudio.preload = 'auto';
    notifAudio.volume = 0.6;
  } catch (err) {
    console.error(err);
    return;
  }

  UNLOCK_EVENTS.forEach((evt) => window.addEventListener(evt, unlockAudio, true));
}

export function isSoundEnabled() {
  try {
    return localStorage.getItem(SOUND_PREF_KEY) !== 'false';
  } catch {
    return true;
  }
}

export function setSoundEnabled(enabled) {
  try {
    localStorage.setItem(SOUND_PREF_KEY, String(!!enabled));
  } catch {
    /* stockage indisponible : préférence non persistée */
  }
}

export function getNotificationPermission() {
  return 'Notification' in window ? Notification.permission : 'denied';
}

export async function requestNotificationPermission() {
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  const permission = await Notification.requestPermission();
  return permission === 'granted';
}

function playSound() {
  if (!notifAudio) return;
  notifAudio.currentTime = 0;
  notifAudio
    .play()
    .then(() => {
      audioUnlocked = true;
    })
    .catch(() => {
      // Audio encore verrouillé (aucun geste utilisateur) : rien à faire.
    });
}

function showOsNotification(title, body, tag) {
  if (getNotificationPermission() !== 'granted') return;
  try {
    const notification = new Notification(title, {
      body,
      icon: '/logo.png',
      // Même tag = la notification remplace la précédente (pas d'empilement,
      // et dédoublonnée entre plusieurs onglets ouverts).
      tag,
      renotify: true,
      silent: true // le son est joué par l'application
    });
    setTimeout(() => notification.close(), OS_NOTIF_TIMEOUT_MS);
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  } catch (err) {
    // Certains navigateurs mobiles exigent un Service Worker pour `new Notification`.
  }
}

/**
 * Signale un événement entrant : son (si activé) et notification système
 * (seulement si l'onglet est en arrière-plan, sinon l'UI suffit).
 */
export function playNotif(title, body, { tag = 'cliclaim-notif' } = {}) {
  if (!isSoundEnabled()) return;

  const now = Date.now();
  if (now - lastPlayedAt < SOUND_THROTTLE_MS) return;
  lastPlayedAt = now;

  playSound();

  if (typeof document !== 'undefined' && document.hidden) {
    showOsNotification(title, body, tag);
  }
}

export function isAudioUnlocked() {
  return audioUnlocked;
}
